import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  ArrowDownUp, ArrowRight, Bookmark, BookmarkPlus, Calendar, ChevronDown, CircleDot, FolderKanban, Info, Layers3, LayoutPanelTop, List, ListFilter, Maximize2, Minimize2, Plus,
  Search, SlidersHorizontal, SquareKanban, StickyNote, Table2, Trash2, UserPlus, UserRound, Users, X, Zap,
} from 'lucide-react';
import type { FilterRule, Project } from '@missions/types';
import { Button, DatePicker, Select, useDialogs, useToast } from '@missions/ui';
import { api } from '../../lib/client';
import { useApi, useErrorToast, useInvalidate } from '../../lib/hooks';
import { setPageTitle } from '../../lib/seo';
import { useSession, useT } from '../../lib/session';
import { AvStack, Empty, Radar, daysTo, fmtDateLong } from '../../components/atoms';
import { Layer } from '../../components/Layer';
import { Menu, type MenuItem } from '../../components/Menu';
import { useChrome, useShell } from '../../layout/shell-context';
import { GoToCard, ProjectTree } from '../../layout/sidebar-parts';
import { useMemberOptions } from '../../features/missions/data';
import { ListView, TableView } from './ListView';
import { BoardView, CalendarView, OverviewView, StickiesPreview } from './OtherViews';
import { DEFAULT_LIST, type GroupBy, type ListState, type Sort, type ViewKey, useProjectData } from './useProjectData';

const VIEWS: [ViewKey, string, ReactNode][] = [
  ['overview', 'Overview', <LayoutPanelTop key="o" />],
  ['list', 'List', <List key="l" />],
  ['board', 'Board', <SquareKanban key="b" />],
  ['calendar', 'Calendar', <Calendar key="c" />],
  ['table', 'Table', <Table2 key="t" />],
  ['stickies', 'Stickies', <StickyNote key="s" />],
];
const FS_VIEWS: ViewKey[] = ['list', 'board', 'calendar', 'table', 'stickies'];
const GROUP_LABEL: Record<GroupBy, string> = { status: 'Status', assignee: 'Assignee', priority: 'Priority', dueAt: 'Due date', none: 'None' };

export function ProjectPage() {
  const { projectId = '', missionId } = useParams();
  if (missionId) return <Navigate to={`/p/${projectId}?m=${missionId}`} replace />;
  return <ProjectInner key={projectId} projectId={projectId} />;
}

function ProjectInner({ projectId }: { projectId: string }) {
  const { project, fields, isPending, isError, refetch } = useProjectData(projectId);
  const t = useT();
  if (isPending)
    return (
      <div className="page">
        <div className="card"><div className="p-load"><Radar size={56} /><span>Loading project…</span></div></div>
      </div>
    );
  if (isError || !project)
    return (
      <div className="page">
        <div className="card">
          <Empty title="We couldn’t open this project" text={`It may have been archived, or you’re not a member yet. Ask a project owner to add you to see its ${t.items}.`} actions={<Button onClick={() => void refetch()}>Try again</Button>} />
        </div>
      </div>
    );
  return <ProjectLoaded project={project} fields={fields} />;
}

function ProjectLoaded({ project, fields }: { project: Project; fields: ReturnType<typeof useProjectData>['fields'] }) {
  const t = useT();
  const s = useSession();
  const shell = useShell();
  const nav = useNavigate();
  const toast = useToast();
  const dialogs = useDialogs();
  const onErr = useErrorToast();
  const invalidate = useInvalidate();
  const members = useMemberOptions();
  const [sp, setSp] = useSearchParams();
  const view = (VIEWS.some(([k]) => k === sp.get('view')) ? sp.get('view') : 'list') as ViewKey;
  const setView = (v: ViewKey) => {
    const n = new URLSearchParams(sp);
    n.set('view', v);
    setSp(n);
    if (!FS_VIEWS.includes(v) && shell.focus) shell.setFocus(false);
  };
  const [st, setSt] = useState<ListState>(DEFAULT_LIST);
  const [filterOpen, setFilterOpen] = useState(false);
  const groupRef = useRef<HTMLButtonElement>(null);
  const sortRef = useRef<HTMLButtonElement>(null);
  const viewsRef = useRef<HTMLButtonElement>(null);
  const [menu, setMenu] = useState<'group' | 'sort' | 'views' | null>(null);
  const savedViews = useApi('views.list', { query: { projectId: project.id, workspaceId: project.workspaceId } } as never);
  const searchRef = useRef<HTMLInputElement>(null);
  useEffect(() => setPageTitle(project.name), [project.name]);
  useEffect(() => {
    const on = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement).tagName?.toLowerCase();
      if (e.key === '/' && !['input', 'textarea'].includes(tag) && view !== 'overview') {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    document.addEventListener('keydown', on);
    return () => document.removeEventListener('keydown', on);
  }, [view]);

  const d = project.dueAt ? daysTo(project.dueAt) : null;
  const launch = d === null ? null : d < 0 ? <span className="ms-t late" data-tooltip={`Launch ${fmtDateLong(project.dueAt!)}`}>+{-d}d late</span> : t.tMinusDates ? <span className={`ms-t ${d <= 2 ? 'soon' : 'ok'}`} data-tooltip={`Launch ${fmtDateLong(project.dueAt!)}`}>T-{d}d to launch</span> : <span className="ms-t ok">Due {fmtDateLong(project.dueAt!)}</span>;

  const share = async () => {
    const email = await dialogs.prompt({ title: `Share “${project.name}”`, message: 'Invite a teammate by email. They’ll get access to this workspace.', label: 'Email address', placeholder: 'name@company.com', confirmText: 'Send invite', validate: (v) => (/^\S+@\S+\.\S+$/.test(v) ? null : 'Enter a valid email address') });
    if (!email) return;
    try {
      const roles = await api.call('roles.matrix');
      const member = roles.roles.find((r) => r.key === 'member') ?? roles.roles[0];
      if (!member) throw new Error('no roles');
      await api.call('invitations.create', { body: { emails: [email], roleId: member.id, workspaceIds: [project.workspaceId] } });
      toast(`Invite sent to ${email}`);
    } catch (e) {
      onErr(e);
    }
  };

  const viewItems: MenuItem[] = [
    { header: 'Saved views' },
    ...((savedViews.data?.data ?? []).map((v) => ({
      label: v.name,
      icon: <Bookmark />,
      onClick: () => {
        setSt({ ...DEFAULT_LIST, rules: v.config.filter.rules, groupBy: v.config.groupBy, sort: v.config.sort, me: !!v.config.filter.quick.me, showClosed: v.config.filter.quick.showClosed ?? true });
        setView((['list', 'board', 'calendar', 'table'].includes(v.type) ? v.type : 'list') as ViewKey);
        toast(`View “${v.name}” applied`, { type: 'info' });
      },
    })) satisfies MenuItem[]),
    '-',
    { label: 'Save current view as…', icon: <BookmarkPlus />, disabled: !s.can('view.manage'), lock: 'Member', onClick: () => void saveView() },
  ];
  const saveView = async () => {
    const name = await dialogs.prompt({ title: 'Save view as…', label: 'View name', placeholder: 'e.g. Due this week', confirmText: 'Save view', validate: (v) => (v.length > 40 ? 'Keep it under 40 characters' : null) });
    if (!name) return;
    try {
      await api.call('views.create', {
        body: {
          name,
          type: view === 'board' || view === 'calendar' || view === 'table' ? view : 'list',
          isShared: true,
          projectId: project.id,
          workspaceId: project.workspaceId,
          config: { filter: { match: 'all', rules: st.rules, quick: { me: st.me, showClosed: st.showClosed } }, groupBy: st.groupBy, sort: st.sort, columns: [] },
        } as never,
      });
      await invalidate('views');
      toast(`View “${name}” saved`);
    } catch (e) {
      onErr(e);
    }
  };

  useChrome(
    {
      section: 'Projects',
      focusable: FS_VIEWS.includes(view),
      current: (
        <>
          <span className="pd" style={{ background: project.color }} />
          <span className="nm">{project.name}</span>
          <span className="sbadge" style={project.archived ? { background: 'var(--surface-3)', color: 'var(--text-2)' } : undefined}>{project.archived ? 'Archived' : 'Active'}</span>
        </>
      ),
      projectActions: (
        <>
          <button className="btn btn-o hide-m" onClick={() => void share()} disabled={!s.can('user.invite')} aria-label="Share" data-tooltip={s.can('user.invite') ? 'Share' : 'Requires Admin role'}>
            <UserPlus /><span className="bl">Share</span>
          </button>
          <button className="btn btn-o hide-m" ref={viewsRef} onClick={() => setMenu(menu === 'views' ? null : 'views')} aria-label="Saved views" data-tooltip="Saved views">
            <Bookmark /><span className="bl">Views</span><ChevronDown className="hide-t" />
          </button>
        </>
      ),
      secondaryTitle: 'Projects',
      secondary: (
        <>
          <div className="s-title">
            <span className="tile" style={{ background: project.color }}><FolderKanban /></span>
            <div style={{ minWidth: 0 }}>
              <b>{project.name}</b>
              <small>{project.client?.name ? `${project.client.name} · ` : ''}{project.counts.total} {t.items}</small>
            </div>
          </div>
          {VIEWS.map(([k, l, i]) => (
            <button key={k} className={`si${view === k ? ' on' : ''}`} onClick={() => setView(k)}>{i}<span className="lbl">{l}</span></button>
          ))}
          <div className="glabel">{t.tMinusDates ? 'Mission' : 'Project'}</div>
          <button className="si" onClick={() => nav('/settings/members')}><Users /><span className="lbl">{t.crew}</span><span className="cnt">{project.members.length}</span></button>
          <button className="si" onClick={() => nav('/settings/fields')}><SlidersHorizontal /><span className="lbl">Custom fields</span><span className="cnt">{fields.length}</span></button>
          <button className="si" onClick={() => setMenu(null)} disabled><CircleDot /><span className="lbl">Statuses</span><span className="cnt">{project.statuses.length}</span></button>
          <button className="si" disabled><Zap /><span className="lbl">Automations</span><span className="soon">Soon</span></button>
          <div className="s-sep" />
          <div className="glabel" style={{ paddingTop: 2 }}>All projects</div>
          <ProjectTree activeId={project.id} />
        </>
      ),
      rail: (
        <>
          <div className="rcard">
            <h4>{t.tMinusDates ? 'Mission' : 'Project'} details</h4>
            <div className="kv"><span>Code</span><span className="ms-code" style={{ color: 'var(--text)' }}>{project.code}</span></div>
            {project.client && <div className="kv"><span>Client</span><b>{project.client.name}</b></div>}
            {project.startAt && <div className="kv"><span>Start</span><b>{fmtDateLong(project.startAt)}</b></div>}
            {project.dueAt && <div className="kv"><span>Launch</span><b>{fmtDateLong(project.dueAt)}</b></div>}
            <div className="kv" style={{ alignItems: 'center' }}>
              <span>Progress</span>
              <b style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, justifyContent: 'flex-end' }}>
                <span className="pbar" style={{ maxWidth: 120 }}><i style={{ width: `${project.progress}%` }} /></span>
                {Math.round(project.progress)}%
              </b>
            </div>
            <div className="kv" style={{ alignItems: 'center' }}><span>{t.crew} ({project.members.length})</span><AvStack users={project.members} size={26} max={5} /></div>
            <button className="golink" style={{ justifyContent: 'center' }} onClick={() => nav('/settings/members')}>Manage members<ArrowRight className="ar" style={{ marginLeft: 0 }} /></button>
          </div>
          <GoToCard />
        </>
      ),
    },
    [project, view, fields.length, savedViews.data, t, menu, s],
  );

  const fsOn = shell.focus;
  const groupItems: MenuItem[] = (
    [
      ['status', 'Status'],
      ['assignee', 'Assignee'],
      ['priority', 'Priority'],
      ['dueAt', 'Due date'],
      ['none', 'No grouping'],
    ] as [GroupBy, string][]
  ).map(([k, l]) => ({ label: l, selected: st.groupBy === k, onClick: () => setSt({ ...st, groupBy: k }) }));
  const sortItems: MenuItem[] = (
    [
      ['manual', 'Manual'],
      ['dueAt', 'Due date (soonest)'],
      ['-dueAt', 'Due date (latest)'],
      ['priority', 'Priority'],
      ['-createdAt', 'Newest first'],
      ['title', 'Name (A–Z)'],
    ] as [Sort, string][]
  ).map(([k, l]) => ({ label: l, selected: st.sort === k, onClick: () => setSt({ ...st, sort: k }) }));
  const sortLabel = sortItems.find((x) => typeof x === 'object' && 'selected' in x && x.selected);
  const nFilters = st.rules.length + (st.assignee ? 1 : 0) + (st.due !== 'any' ? 1 : 0) + (st.me ? 1 : 0);

  let content: ReactNode;
  if (view === 'overview') content = <OverviewView project={project} />;
  else if (view === 'board') content = <BoardView project={project} state={st} />;
  else if (view === 'calendar') content = <CalendarView project={project} state={st} />;
  else if (view === 'table') content = <TableView project={project} fields={fields} state={st} />;
  else if (view === 'stickies') content = <StickiesPreview project={project} />;
  else content = <ListView project={project} fields={fields} state={st} />;

  const viewMeta = VIEWS.find(([k]) => k === view)!;
  return (
    <div className="page" id="pgProject">
      {fsOn && (
        <div className="fbar" role="toolbar" aria-label="Full screen toolbar">
          <span className="dot" style={{ background: project.color }} />
          <b className="fb-name" data-tooltip={project.name}>{project.name}</b>
          <div className="fb-seg" role="tablist" aria-label="Views">
            {VIEWS.filter(([k]) => ['list', 'board', 'calendar', 'table'].includes(k)).map(([k, l, i]) => (
              <button key={k} className={view === k ? 'on' : ''} role="tab" aria-selected={view === k} data-tooltip={l} aria-label={l} onClick={() => setView(k)}>{i}<span>{l}</span></button>
            ))}
          </div>
          <span className="sp" />
          <button className="ib" aria-label="Filter" data-tooltip="Filter" onClick={() => setFilterOpen(true)}><ListFilter /></button>
          {s.can('mission.create') && (
            <button className="ui-btn primary fb-new" onClick={() => shell.openNewMission({ projectId: project.id })} aria-label={t.newItem}><Plus /><span>{t.newItem}</span></button>
          )}
          <button className="ib" aria-label="Exit full screen" data-tooltip="Exit full screen" data-shortcut="Esc" onClick={() => shell.setFocus(false)}><Minimize2 /></button>
        </div>
      )}
      <div className="mhead">
        <div className="ms-ring" style={{ ['--p' as string]: project.progress }} data-tooltip={`${Math.round(project.progress)}% complete`}><span>{Math.round(project.progress)}%</span></div>
        <div className="mt">
          <b data-tooltip={project.name}>{project.name}</b>
          <div className="meta">
            <span className="ms-code">{project.code}</span>
            {project.client && <span>{project.client.name}</span>}
            {launch}
          </div>
        </div>
        <div className="crew">
          <small>{t.crew} ({project.members.length})</small>
          <AvStack users={project.members} size={26} max={5} />
        </div>
      </div>
      <div className="card" id="projCard">
        <div className="card-h">
          <span className="tile">{viewMeta[2]}</span>
          <h2>
            {view === 'list' || view === 'table' ? t.Items : viewMeta[1]}
            <button className="info" aria-label="About this view" data-tooltip={`${project.counts.total} ${t.items} in this project`}><Info /></button>
          </h2>
          <span className="sub">{project.counts.total} {t.items} · {project.counts.open} open</span>
          <span className="sp" />
          {FS_VIEWS.includes(view) && (
            <button className="ib fs-btn" aria-pressed={fsOn} aria-label={fsOn ? 'Exit full screen' : 'Full screen'} data-tooltip={fsOn ? 'Exit full screen' : 'Full screen'} data-shortcut={fsOn ? 'Esc' : 'F'} onClick={() => shell.setFocus(!fsOn)}>
              {fsOn ? <Minimize2 /> : <Maximize2 />}
            </button>
          )}
        </div>
        <div className="segv" role="tablist" aria-label="Views">
          {VIEWS.map(([k, l, i]) => (
            <button key={k} className={view === k ? 'on' : ''} role="tab" aria-selected={view === k} onClick={() => setView(k)}>{i}{l}</button>
          ))}
        </div>
        {view !== 'overview' && view !== 'stickies' && (
          <div className="ctrls">
            <label className="search">
              <Search />
              <span className="sr">Search {t.items}</span>
              <input ref={searchRef} type="search" placeholder={`Search ${t.items}…`} autoComplete="off" value={st.q} onChange={(e) => setSt({ ...st, q: e.target.value })} />
              {st.q && <button className="clr" onClick={() => setSt({ ...st, q: '' })} aria-label="Clear search"><X /></button>}
            </label>
            <Select size="sm" value={st.assignee ?? 'all'} onChange={(v) => setSt({ ...st, assignee: !v || v === 'all' ? null : v })} options={[{ value: 'all', label: 'All assignees' }, ...members.options]} searchable aria-label="Assignee filter" title="Assignee" />
            <Select
              size="sm"
              value={st.due}
              onChange={(v) => setSt({ ...st, due: (v ?? 'any') as ListState['due'] })}
              options={[{ value: 'any', label: 'Any due date' }, { value: 'overdue', label: 'Overdue' }, { value: 'today', label: 'Due today' }, { value: 'this_week', label: 'This week' }, { value: 'no_date', label: 'No due date' }]}
              aria-label="Due date filter"
              title="Due date"
            />
            <button className={`chip${nFilters ? ' on' : ''}`} onClick={() => setFilterOpen(true)} data-tooltip={`Filter ${t.items}`} aria-label="Filter">
              <ListFilter /><span className="lbl-t">Filter</span>{nFilters > 0 && <span className="n">{nFilters}</span>}
            </button>
            {(view === 'list') && (
              <button className="chip hide-m" ref={groupRef} onClick={() => setMenu(menu === 'group' ? null : 'group')} aria-label="Group by">
                <Layers3 /><span className="lbl-t">Group: {GROUP_LABEL[st.groupBy]}</span>
              </button>
            )}
            <button className="chip" ref={sortRef} onClick={() => setMenu(menu === 'sort' ? null : 'sort')} data-tooltip={`Sort ${t.items}`} aria-label="Sort">
              <ArrowDownUp /><span className="lbl-t">Sort: {(sortLabel as { label?: string } | undefined)?.label ?? 'Manual'}</span>
            </button>
            <button className={`chip${st.me ? ' on' : ''}`} aria-pressed={st.me} onClick={() => setSt({ ...st, me: !st.me })} data-tooltip={`Show only my ${t.items}`} aria-label={`Show only my ${t.items}`}>
              <UserRound /><span className="lbl-t">Me</span>
            </button>
            <span className="sp" />
            <label className="swl hide-m">
              <input type="checkbox" className="sr" checked={st.showClosed} onChange={(e) => setSt({ ...st, showClosed: e.target.checked })} />
              <span className="sw" />
              {t.showClosed}
            </label>
          </div>
        )}
        <Menu anchorRef={groupRef} open={menu === 'group'} onClose={() => setMenu(null)} title="Group by" items={groupItems} />
        <Menu anchorRef={sortRef} open={menu === 'sort'} onClose={() => setMenu(null)} title="Sort by" items={sortItems} />
        <Menu anchorRef={viewsRef} open={menu === 'views'} onClose={() => setMenu(null)} title="Views" items={viewItems} alignRight />
        <div id="content">{content}</div>
      </div>
      <FilterPopover open={filterOpen} onClose={() => setFilterOpen(false)} state={st} setState={setSt} onSave={() => void saveView()} statuses={project.statuses} />
    </div>
  );
}

/* ---------- Filter rule builder (popover on desktop, bottom sheet on phones) ---------- */
const FIELDS: { value: FilterRule['field']; label: string }[] = [
  { value: 'status', label: 'Status' },
  { value: 'assignee', label: 'Assignee' },
  { value: 'priority', label: 'Priority' },
  { value: 'dueAt', label: 'Due date' },
  { value: 'title', label: 'Name' },
];
const OPS: Record<string, { value: FilterRule['op']; label: string }[]> = {
  status: [{ value: 'in', label: 'is any of' }, { value: 'not_in', label: 'is not' }],
  assignee: [{ value: 'in', label: 'is any of' }, { value: 'is_empty', label: 'is empty' }],
  priority: [{ value: 'in', label: 'is any of' }, { value: 'not_in', label: 'is not' }],
  dueAt: [{ value: 'before', label: 'is before' }, { value: 'after', label: 'is after' }, { value: 'is_empty', label: 'is empty' }],
  title: [{ value: 'contains', label: 'contains' }],
};

function FilterPopover({ open, onClose, state, setState, onSave, statuses }: { open: boolean; onClose: () => void; state: ListState; setState: (s: ListState) => void; onSave: () => void; statuses: Project['statuses'] }) {
  const t = useT();
  const s = useSession();
  const members = useMemberOptions();
  const [rules, setRules] = useState<FilterRule[]>(state.rules);
  useEffect(() => {
    if (open) setRules(state.rules);
  }, [open, state.rules]);
  const statusOpts = useMemo(() => statuses.map((x) => ({ value: x.id, label: x.name, color: x.color })), [statuses]);
  const valueControl = (r: FilterRule, i: number) => {
    const set = (v: FilterRule['value']) => setRules(rules.map((x, j) => (j === i ? { ...x, value: v } : x)));
    if (r.op === 'is_empty') return <span style={{ color: 'var(--text-3)' }}>—</span>;
    if (r.field === 'assignee') return <Select multiple size="sm" value={Array.isArray(r.value) ? r.value.map(String) : []} onChange={set} options={members.options} searchable aria-label="Value" placeholder="Choose people" />;
    if (r.field === 'priority')
      return <Select multiple size="sm" value={Array.isArray(r.value) ? r.value.map(String) : []} onChange={set} options={[{ value: 'urgent', label: 'Urgent' }, { value: 'high', label: 'High' }, { value: 'normal', label: 'Normal' }, { value: 'low', label: 'Low' }]} aria-label="Value" placeholder="Choose priority" />;
    if (r.field === 'dueAt') return <DatePicker size="sm" value={typeof r.value === 'string' ? r.value : null} onChange={set} aria-label="Value" />;
    if (r.field === 'status') return <Select multiple size="sm" value={Array.isArray(r.value) ? r.value.map(String) : []} onChange={set} options={statusOpts} aria-label="Value" placeholder="Choose status" />;
    return <input className="ui-input" style={{ height: 34 }} value={typeof r.value === 'string' ? r.value : ''} onChange={(e) => set(e.target.value)} aria-label="Value" placeholder="Type to match" />;
  };
  return (
    <Layer open={open} onRequestClose={onClose} className="fpop" clear label="Filters" style={{ top: 140, right: 24 }}>
      <div className="sheet-grab" />
      <div className="fp-h">
        <ListFilter style={{ color: 'var(--brand)' }} />
        <b>Filters</b>
        <span style={{ color: 'var(--text-3)', fontSize: '0.7812rem' }}>{rules.length} rule{rules.length === 1 ? '' : 's'}</span>
        <button className="ib x" onClick={onClose} aria-label="Close filters"><X /></button>
      </div>
      <div className="fp-b">
        {rules.length === 0 && <div style={{ color: 'var(--text-3)', padding: '8px 2px' }}>No filters yet. Add one to narrow down {t.items}.</div>}
        {rules.map((r, i) => (
          <div className="rule" key={i}>
            <span className="conj">{i === 0 ? 'Where' : 'And'}</span>
            <Select size="sm" value={r.field} onChange={(v) => v && setRules(rules.map((x, j) => (j === i ? { field: v, op: OPS[v]?.[0]?.value ?? 'is', value: null } : x)))} options={FIELDS.map((f) => ({ value: f.value, label: f.label }))} aria-label="Field" />
            <Select size="sm" value={r.op} onChange={(v) => v && setRules(rules.map((x, j) => (j === i ? { ...x, op: v } : x)))} options={OPS[r.field] ?? [{ value: 'is', label: 'is' }]} aria-label="Condition" />
            <div className="rv">{valueControl(r, i)}</div>
            <button className="ib" aria-label="Remove filter" data-tooltip="Remove filter" onClick={() => setRules(rules.filter((_, j) => j !== i))}><Trash2 /></button>
          </div>
        ))}
      </div>
      <div className="fp-add">
        <button className="btn btn-g" onClick={() => setRules([...rules, { field: 'assignee', op: 'in', value: [] }])}><Plus />Add filter</button>
      </div>
      <div className="fp-f">
        <button className="btn btn-g" disabled={!s.can('view.manage')} onClick={onSave}><BookmarkPlus />Save as view</button>
        <span className="sp" />
        <button className="btn btn-o" onClick={() => setRules([])}>Clear all</button>
        <button
          className="btn btn-p"
          onClick={() => {
            setState({ ...state, rules });
            onClose();
          }}
        >
          Apply filters
        </button>
      </div>
    </Layer>
  );
}
